import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useSelector, shallowEqual } from 'react-redux';
import dayjs from 'dayjs';

import { anyTrue } from 'utils/misc';
import { getFuelValue } from 'components/direct-sales/fuel-type/getFuelValue';
import {
  selectProducts,
  selectTariffEndsOn,
  selectCurrentProduct
} from 'state/quote/quote-slice';
import { selectData as selectSalesOrderData } from 'state/sales-order/sales-order-slice';
import { useMeterType } from 'hooks/useMeterType';
import { selectMarketingPreferences } from 'state/user-preferences/user-preferences-slice';
import { selectHasPsr } from 'state/psr/psr-slice';
import { useConsumption } from 'hooks/useConsumption';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { selectElectricVehicle } from 'state/electric-vehicle/electric-vehicle-slice';
import { selectPostcode } from 'state/postcode/postcode-slice';
import { selectedAddress } from 'state/selected-address/selected-address-slice';
import { usePartner } from 'components/app/routes/usePartner';
import { useJourneyType } from './useJourneyType';
import { useElecMultirate } from 'hooks/useElecMultirate';
import { useHasSmartMeter } from 'components/direct-sales/quote/hooks/useHasSmartMeter';
import { selectConsumptionType } from 'state/consumption-entry/consumption-entry-slice';
import { usePaymentMethod } from 'hooks/usePaymentMethod';
import { selectIsDualRate } from 'state/quote/quote-slice';
import { FUEL_OPTIONS, PARTNERS } from 'config/app-config';

export const defaultToEmptyString = (value) => (value ? value : '');

const useSelectedMeterType = () => {
  const { hasGasSmartMeter, hasElectricitySmartMeter } = useHasSmartMeter();
  const { electricityMeter, gasMeter } = useMeterType();

  return !electricityMeter && !gasMeter
    ? ''
    : [
        hasElectricitySmartMeter ? 'smart' : electricityMeter?.smetsVersion,
        hasGasSmartMeter ? 'smart' : gasMeter?.smetsVersion
      ];
};
export const useEnergyData = () => {
  const { pathname } = useLocation();
  const { consumption } = useConsumption();
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const consumptionType = useSelector(selectConsumptionType, shallowEqual);
  const currentProduct = useSelector(selectCurrentProduct, shallowEqual);
  const hasPsr = useSelector(selectHasPsr, shallowEqual);
  const products = useSelector(selectProducts, shallowEqual);
  const tariffEndsOn = useSelector(selectTariffEndsOn, shallowEqual);
  const { transactionId } = useSelector(selectSalesOrderData, shallowEqual);
  const { paymentName } = usePaymentMethod();
  const marketingPrefferences = useSelector(
    selectMarketingPreferences,
    shallowEqual
  );
  const postcode = useSelector(selectPostcode, shallowEqual);
  const electricVehicle = useSelector(selectElectricVehicle, shallowEqual);
  const address = useSelector(selectedAddress, shallowEqual);
  const today = dayjs().format('DD/MM/YYYY');
  const { partnerPath, activePartner } = usePartner();
  const journeyType = useJourneyType();
  const { showTOUTariffs } = useElecMultirate();
  const choseDualRateE7 = useSelector(selectIsDualRate, shallowEqual);

  const { gas: gasConsumption, electric: electricityConsumption } = consumption;
  const selectedFuelType =
    getFuelValue(fuelType) === FUEL_OPTIONS.DUAL
      ? 'Gas & Electricity'
      : getFuelValue(fuelType);

  const shownTariffs =
    products &&
    products.map(({ name, annualAmount, monthlyAmount }) => ({
      name: defaultToEmptyString(name),
      annualPrice: defaultToEmptyString(annualAmount),
      monthlyPrice: defaultToEmptyString(monthlyAmount)
    }));
  const marketingOptIn = anyTrue(marketingPrefferences);

  const selectedMeterType = useSelectedMeterType();
  const isDirectSales = activePartner[PARTNERS.DIRECT_SALES];

  return useMemo(
    () => ({
      step: pathname,
      journeyType,
      postcode: defaultToEmptyString(postcode),
      address_value: defaultToEmptyString(address?.displayName),
      energyUsageKWh: [
        defaultToEmptyString(gasConsumption),
        defaultToEmptyString(electricityConsumption)
      ],
      aboutProperty: defaultToEmptyString(consumptionType),
      selectedFuelType: defaultToEmptyString(selectedFuelType),
      shownTariffs,
      selectedProduct: {
        name: defaultToEmptyString(currentProduct.name),
        annualPrice: defaultToEmptyString(currentProduct.annualAmount),
        monthlyPrice: defaultToEmptyString(currentProduct.monthlyAmount)
      },
      selected_tariff_end: defaultToEmptyString(tariffEndsOn),
      selectedPaymentType: paymentName,
      dateOfSale: today,
      salesOrderNo: defaultToEmptyString(transactionId),
      selectedMeterType,
      marketingOptIn,
      priority_register: defaultToEmptyString(hasPsr),
      isEnergyCustomer: false, // if customer is or has logged in and has an energy product holding
      isLoggedIn: false,
      evParam: electricVehicle ? 'BGEV' : '',
      isTOUEligible: showTOUTariffs,
      'services-data': {
        contractNumber: '',
        products: '',
        serviceContractProcessed: '',
        serviceOrderStatus: ''
      },
      acquisition_type: !isDirectSales ? partnerPath : '',
      meterRateType: choseDualRateE7 ? 'dual rate' : 'single rate'
    }),
    [
      electricityConsumption,
      selectedFuelType,
      gasConsumption,
      paymentName,
      pathname,
      journeyType,
      postcode,
      address?.displayName,
      consumptionType,
      shownTariffs,
      currentProduct.name,
      currentProduct.annualAmount,
      currentProduct.monthlyAmount,
      tariffEndsOn,
      today,
      transactionId,
      selectedMeterType,
      marketingOptIn,
      hasPsr,
      electricVehicle,
      showTOUTariffs,
      partnerPath,
      choseDualRateE7,
      isDirectSales
    ]
  );
};
